import React, { type FC, Fragment } from 'react';
import { connect, type MapStateToProps } from 'react-redux';
import classNames from 'classnames';
import { compose } from '@reduxjs/toolkit';

import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { getSportKey } from 'app/lib/betting/sport-utils';
import { GridHeader } from 'app/modules/Betting';
import type { TournamentWithHierarchyBySportId } from 'app/redux/betting-presentation/grid/selectors';
import ErrorBoundary from 'app/widgets/ErrorBoundary';

import { TournamentTable } from '../TournamentTable';

import styles from './SportGrid.module.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BettingContextProps = any;

type StoreProps = {
  tournamentsBySportId: TournamentWithHierarchyBySportId;
};

const SportsGridWithoutHOC = ({
  tournamentsBySportId,
}: StoreProps) => {
  const sportIds = Array.from(tournamentsBySportId.keys());

  return (
    <ErrorBoundary>
      <div className={styles.content}>
        {sportIds.map((sportId) => {
          const matchIdsByTournamentId = tournamentsBySportId.get(sportId);
          const tournamentIds = Array.from(matchIdsByTournamentId?.keys() || []);
          const sportContainerClassName = classNames(getSportKey(sportId), styles.sportContainer);

          return (
            <Fragment key={sportId}>
              <GridHeader sportId={sportId}
                          shouldBeLink />

              <div className={sportContainerClassName}>
                {tournamentIds.map((tournamentId, index) => {
                  const matchIds = matchIdsByTournamentId?.get(tournamentId)?.matchIds;

                  return matchIds && (
                    <TournamentTable key={tournamentId}
                                     sportId={sportId}
                                     tournamentId={tournamentId}
                                     showGridColumns={index === 0}
                                     matchIds={matchIds} />
                  );
                })}
              </div>
            </Fragment>
          );
        })}
      </div>
    </ErrorBoundary>
  );
};

const createMapStateToProps: MapStateToProps<
  StoreProps,
  BettingContextProps,
  ReduxState
> = (state: ReduxState, {
  $betting: {
    grid: {
      selectors: {
        tournamentsGroupedByPromotionLevelBySportIdsSortingByPrioritySelector,
      },
    },
  },
}) => {
  return {
    tournamentsBySportId:
      tournamentsGroupedByPromotionLevelBySportIdsSortingByPrioritySelector(state),
  };
};

export const SportsGrid = compose<FC>(
  withinBettingContext,
  connect(createMapStateToProps),
)(SportsGridWithoutHOC);
